import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Eye, EyeOff } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	password: Yup.string().min(5, "Muito curto").max(50, "Muito longo"),
	email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
});

const ESCOPO_EM_PALAVRAS = {
	own: "vê apenas os atendimentos que estiverem com ela",
	team: "vê os atendimentos da equipe dela",
	queues: "vê os atendimentos das filas em que participa",
	company: "vê todos os atendimentos da empresa",
};

const resumoDoCargo = (cargo) => {
	if (!cargo) {
		return "Sem cargo, a pessoa entra sem permissão nenhuma. Escolha um.";
	}

	const escopo = ESCOPO_EM_PALAVRAS[cargo.ticketScope];
	const aviso =
		cargo.ticketScope === "own"
			? " Atenção: ela não recebe conversa nova da fila sozinha."
			: "";

	return `Com este cargo, a pessoa ${escopo}.${aviso}`;
};

const UserModal = ({ open, onClose, userId }) => {
	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState("");
	const { loading, whatsApps } = useWhatsApps();

	// Cargo: define as permissões e o que a pessoa enxerga na lista de
	// atendimentos. Sem este campo, a tela de Cargos não servia para nada —
	// dava para criar cargos e ninguém podia ser posto neles.
	const [cargos, setCargos] = useState([]);
	const [roleId, setRoleId] = useState("");

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser((prevState) => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map((queue) => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : "");
				setRoleId(data.roleId ? String(data.roleId) : "");
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/roles");
				setCargos(data || []);
			} catch (err) {
				// Sem a lista, o campo some — não é motivo para impedir de
				// salvar o resto do cadastro.
			}
		})();
	}, [open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const handleSaveUser = async (values) => {
		const userData = { ...values, whatsappId, queueIds: selectedQueueIds };
		try {
			// O cargo tem rota própria (com registro de quem mudou), então vai
			// numa chamada separada — depois de existir o usuário.
			let alvo = userId;

			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				const { data } = await api.post("/users", userData);
				alvo = data?.id;
			}

			if (alvo) {
				await api.put(`/users/${alvo}/role`, {
					roleId: roleId ? Number(roleId) : null,
				});
			}

			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{userId
							? i18n.t("userModal.title.edit")
							: i18n.t("userModal.title.add")}
					</DialogTitle>
				</DialogHeader>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, setFieldValue, touched, errors, isSubmitting }) => (
						<Form className="space-y-4">
							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="name">{i18n.t("userModal.form.name")}</Label>
									<Field as={Input} id="name" name="name" autoFocus />
									{touched.name && errors.name && (
										<p className="text-xs text-destructive">{errors.name}</p>
									)}
								</div>
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="password">{i18n.t("userModal.form.password")}</Label>
									<div className="relative">
										<Field
											as={Input}
											id="password"
											name="password"
											type={showPassword ? "text" : "password"}
											className="pr-9"
										/>
										<button
											type="button"
											onClick={() => setShowPassword((e) => !e)}
											className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
										>
											{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
										</button>
									</div>
									{touched.password && errors.password && (
										<p className="text-xs text-destructive">{errors.password}</p>
									)}
								</div>
							</div>

							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="email">{i18n.t("userModal.form.email")}</Label>
									<Field as={Input} id="email" name="email" />
									{touched.email && errors.email && (
										<p className="text-xs text-destructive">{errors.email}</p>
									)}
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<div className="flex-1 space-y-1.5">
											<Label>{i18n.t("userModal.form.profile")}</Label>
											<Select
												value={values.profile}
												onValueChange={(v) => setFieldValue("profile", v)}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="admin">Admin</SelectItem>
													<SelectItem value="user">User</SelectItem>
												</SelectContent>
											</Select>
										</div>
									)}
								/>
							</div>

							<Can
								role={loggedInUser.profile}
								perform="user-modal:editProfile"
								yes={() =>
									cargos.length > 0 && (
										<div className="space-y-1.5">
											<Label>Cargo</Label>
											<Select
												value={roleId || "none"}
												onValueChange={(v) => setRoleId(v === "none" ? "" : v)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sem cargo" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">Sem cargo</SelectItem>
													{cargos.map((cargo) => (
														<SelectItem key={cargo.id} value={String(cargo.id)}>
															{cargo.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{/* O que o cargo escolhido significa, em uma frase: quem
											    cadastra usuário não deveria precisar abrir outra tela
											    para descobrir. */}
											<p className="text-xs leading-relaxed text-muted-foreground">
												{resumoDoCargo(
													cargos.find((c) => String(c.id) === String(roleId))
												)}
											</p>
										</div>
									)
								}
							/>

							<Can
								role={loggedInUser.profile}
								perform="user-modal:editQueues"
								yes={() => (
									<QueueSelect
										selectedQueueIds={selectedQueueIds}
										onChange={(values) => setSelectedQueueIds(values)}
									/>
								)}
							/>
							<Can
								role={loggedInUser.profile}
								perform="user-modal:editQueues"
								yes={() =>
									!loading && (
										<div className="space-y-1.5">
											<Label>{i18n.t("userModal.form.whatsapp")}</Label>
											<Select
												value={whatsappId ? String(whatsappId) : "none"}
												onValueChange={(v) => setWhatsappId(v === "none" ? "" : v)}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">&nbsp;</SelectItem>
													{whatsApps.map((whatsapp) => (
														<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
															{whatsapp.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)
								}
							/>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{userId
										? i18n.t("userModal.buttons.okEdit")
										: i18n.t("userModal.buttons.okAdd")}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default UserModal;
